// eslint-disable-next-line
import { Checkbox, TextInput, Tooltip } from '@mantine/core';
import {
    IconCheckbox,
    IconCircleMinus,
    IconCirclePlus,
    IconInfoCircle,
} from '@tabler/icons-react';
import clsx from 'clsx';
import { ReactNode, useEffect, useState } from 'react';
import styles from './building-blocks.module.css';

export type DivProps = React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
>;

export const ExpandableForm: React.FC<
    {
        expand: boolean;
        setExpand: (b: boolean) => void;
        label: ReactNode;
        children?: ReactNode;
        showAddedLabel?: boolean;
    } & DivProps
> = ({ expand, setExpand, label, children, showAddedLabel, ...props }) => {
    return (
        <div className={styles.expandableForm} {...props}>
            <div
                className={clsx(styles.expandIcon, expand && styles.minus)}
                onClick={() => setExpand(!expand)}
            >
                {expand ? <IconCircleMinus /> : <IconCirclePlus />}
            </div>
            <FormBox
                title={label}
                expand={expand}
                setExpand={setExpand}
                showAddedLabel={showAddedLabel}
            >
                {children}
            </FormBox>
        </div>
    );
};

export const BooleanForm: React.FC<
    {
        value: boolean;
        setValue: (b: boolean) => void;
        label: ReactNode;
        tooltipContent?: string;
    } & DivProps
> = ({ value, setValue, label, tooltipContent, ...props }) => {
    return (
        <div className={styles.expandableForm} {...props}>
            <div
                className={clsx(styles.expandIcon, value && styles.minus)}
                onClick={() => setValue(!value)}
            >
                {value ? <IconCircleMinus /> : <IconCirclePlus />}
            </div>
            <div className={styles.booleanForm}>
                <div className={styles.title}>
                    {label}
                    {tooltipContent && (
                        <Tooltip
                            label={tooltipContent}
                            style={{
                                textWrap: 'wrap',
                            }}
                            withArrow
                            position="bottom"
                            arrowSize={8}
                            w="400px"
                            bg="#D2E4F4"
                            c="#01407A"
                            fz="16px"
                            radius={'xl'}
                            p="xl"
                        >
                            <IconInfoCircle
                                style={{ marginLeft: '4px' }}
                                size={20}
                            />
                        </Tooltip>
                    )}
                </div>
                {value && <AddedLabel />}
            </div>
        </div>
    );
};

const AddedLabel: React.FC<{}> = ({}) => {
    return (
        <div
            style={{
                color: 'black',
                justifyContent: 'center',
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                gap: '4px',
                borderRadius: '8px',
                fontSize: '12px',
                border: '1px solid black',
                backgroundColor: 'rgba(0, 128, 0, 0.2)',
                padding: '3px 6px',
            }}
        >
            <IconCheckbox size={16} />
            <span>Added</span>
        </div>
    );
};

const FormBox: React.FC<{
    title: ReactNode;
    expand: boolean;
    setExpand: (b: boolean) => void;
    children?: ReactNode;
    showAddedLabel?: boolean;
}> = ({ title, expand, setExpand, children, showAddedLabel }) => {
    return (
        <div
            className={clsx(
                styles.formBox,
                expand ? styles.expanded : styles.collapsed
            )}
            {...(expand ? undefined : { onClick: () => setExpand(!expand) })}
        >
            <div
                className={styles.title}
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                }}
            >
                {title} {showAddedLabel && <AddedLabel />}
            </div>
            {children}
        </div>
    );
};

export const FormInputs: React.FC<{
    expand: boolean;
    children?: ReactNode;
}> = ({ expand, children }) => {
    // Support slide up animation.
    const [localExpand, setLocalExpand] = useState(expand);

    useEffect(() => {
        if (!expand && localExpand) {
            setTimeout(() => setLocalExpand(false), 500);
        } else {
            setLocalExpand(expand);
        }
    }, [expand, localExpand]);

    return (
        <>{localExpand && <div className={styles.inputs}>{children}</div>}</>
    );
};

export const InputStyleClassName = styles.input;

export const InputBigLabel = styles.inputBigLabel;

export const NumberInput: React.FC<{
    label: ReactNode;
    value: string;
    setValue: (s: string) => void;
    className?: string;
}> = ({ label, value, setValue, className }) => {
    return (
        <TextInput
            className={clsx(InputStyleClassName, className)}
            type="number"
            label={label}
            value={value}
            onChange={(e) =>
                setValue(
                    e.target.value === '' ? '' : String(Number(e.target.value))
                )
            }
        />
    );
};

export const BooleanInput: React.FC<{
    label: ReactNode;
    value: boolean;
    setValue: (n: boolean) => void;
    className?: string;
}> = ({ label, value, setValue, className }) => {
    // For checkboxes, we must not use `undefined`, otherwise it will assume
    // that the control is unmanaged, which is not true.

    return (
        <Checkbox
            className={clsx(InputStyleClassName, className)}
            label={label}
            checked={value ?? false}
            onChange={(e) => setValue(e.target.checked)}
        />
    );
};

export const RadioInput: React.FC<{
    label: ReactNode;
    checked: boolean;
    onCheck: () => void;
    className?: string;
}> = ({ label, checked, onCheck, className }) => {
    // The mantine radio button cannot be customized easily, so just build our
    // own to match figma.
    return (
        <div className={clsx(styles.radio, className)}>
            <div
                className={clsx(styles.outerCircle, checked && styles.checked)}
                onClick={onCheck}
            >
                <div className={styles.innerCircle} />
            </div>
            <div onClick={onCheck}>{label}</div>
        </div>
    );
};

export const Row: React.FC<{ children?: ReactNode }> = ({ children }) => {
    return <div className={styles.row}>{children}</div>;
};

export const MiddleLine: React.FC = () => {
    return (
        <div className={styles.middleLine}>
            <div />
            <div />
        </div>
    );
};
